<script setup lang="ts">
import { computed } from "vue";

import type { SearchStickiesQuery } from "@/backend/read/BackendReadSession";
import ListBanner from "@/components/ui/ComplexList/ListBanner.vue";
import ListGroup from "@/components/ui/ComplexList/ListGroup.vue";
import ListGroupHeader from "@/components/ui/ComplexList/ListGroupHeader.vue";
import ListHeaderContent from "@/components/ui/ComplexList/ListHeaderContent.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { SearchResult } from "@/model/card";
import type { SearchResultKey } from "@/model/search";
import { goToSticky } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";

import GroupedByTypeSearchResults from "./GroupedByTypeSearchResults.vue";
import { groupResultsByArt } from "./groupSearchResults";

const props = defineProps<{ query: SearchStickiesQuery; results: readonly SearchResult[] }>();

const resultsByArt = computed(() =>
  groupResultsByArt(props.results).sort((a, b) => {
    if (a.artId === currentArtId.value) {
      return -1;
    }
    if (b.artId === currentArtId.value) {
      return 1;
    }
    return 0;
  }),
);

const currentBoardId = computed(() => useBoardStore().currentBoard().id);
const currentArtId = computed(() => useBoardsStore().boardById(currentBoardId.value).artId);

function select(item: SearchResultKey) {
  void goToSticky(item.id, useBoardsStore().boardById(item.boardId!), "enlarge");
}
</script>

<template>
  <ListGroup class="other-boards-search-results">
    <template #header>
      <ListBanner as="div">
        <template #before>
          <SvgIcon name="general/search-results" width="16" height="16" />
        </template>
        {{ $t("searchResults.resultsBoards") }}
      </ListBanner>
    </template>

    <!-- Results (current art first) -->
    <template v-for="art in resultsByArt" :key="art.artId">
      <ListGroup v-for="board in art.boards" :key="board.boardId">
        <template #header>
          <ListGroupHeader class="results-group-header">
            <ListHeaderContent
              v-if="art.artName.trim()"
              icon-before="general/art"
              :text="art.artName"
            />
            <ListHeaderContent sub :text="board.boardName" />
            <template #after> {{ $t("searchResultsTs.stickies", board.cards.length) }} </template>
          </ListGroupHeader>
        </template>
        <GroupedByTypeSearchResults
          :items="board.cards"
          :board-id="board.boardId"
          @select="select"
        />
      </ListGroup>
    </template>
  </ListGroup>
</template>

<style lang="scss" scoped>
.other-boards-search-results {
  margin-top: 0 !important;

  .results-group-header {
    // Align sticky note count to the top of the header
    :deep(.right) {
      align-self: flex-start;
    }
  }
}
</style>
